import React, { MouseEventHandler, useEffect, useState } from "react";
import { CardContent, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { SUPPORT_MODAL_TITLE } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { usePostReLogin, usePostSupportRequest } from "@/api/userApi";
import { useUserContext } from "@/contexts/userContext";
import { useToastHelper } from "@/helpers/helperFunctions";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  technicalSupportModalAtom,
  welcomeTourStatus,
} from "@/Atoms/modal/DialogueAtoms";
import { useRecoilState } from "recoil";
import { Button } from "../ui/button";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAppDetails } from "@/contexts/AppDetailsContext";

const TechnicalRequestModal = () => {
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState<any>();
  const router = useRouter();
  const { getUserType } = useSubscription();
  const [status, setStatus] = useRecoilState(welcomeTourStatus);

  const {
    mutateAsync: postSupportRequest,
    isPending: messageLoader,
    isSuccess: messegeSuccess,
    data: messegeData,
  } = usePostSupportRequest();

  const { mutateAsync: reLogin } = usePostReLogin();

  const { user, setUser } = useUserContext();
  const { toast } = useToastHelper();
  const { t } = useTranslation("support-page");
  const { details } = useAppDetails();
  const [showTechnicalRequest, setShowTechnicalRequest] = useRecoilState(
    technicalSupportModalAtom
  );
  const title = showTechnicalRequest.isRequest
    ? SUPPORT_MODAL_TITLE.REQUEST_ACCESS
    : showTechnicalRequest.isContactSales
    ? SUPPORT_MODAL_TITLE.CONTACT_SALES
    : SUPPORT_MODAL_TITLE.TECHNICAL_SUPPORT;
  const onSubmit: MouseEventHandler<HTMLButtonElement> = async (e) => {
    setMessage(message.trim() || "");
    if (!message?.trim?.()) {
      setMessageError(true);
      return;
    }
    setMessageError(false);
    await postSupportRequest({
      user_id: user?.user_id,
      messege: message?.trim?.(),
      source: showTechnicalRequest.isRequest
        ? "request_access"
        : showTechnicalRequest.isContactSales
        ? "enterprise"
        : undefined,
    });
    if (showTechnicalRequest.isRequest) {
      const res = await reLogin();
      setUser(res.data);
      setShowTechnicalRequest({ isOpen: false });
      getUserType(user);
      setStatus("SUCCESS");
    }
  };

  useEffect(() => {
    if (messegeSuccess && !showTechnicalRequest.isRequest) {
      toast.success(messegeData.data.message);
      setShowTechnicalRequest({
        isOpen: false,
        isRequest: false,
        helpline: false,
        isContactSales: false,
      });
    }
  }, [messegeSuccess, messageLoader]);

  return (
    <Dialog
      open={true}
      onOpenChange={() => {
        setShowTechnicalRequest({
          isOpen: false,

          isRequest: false,
          isContactSales: false,
          helpline: false,
        });
        setMessageError(false);
      }}
    >
      <DialogContent className="py-2 md:h-auto h-[100dvh]">
        <CardHeader className="px-0 ">
          <CardTitle className="text-2xl whitespace-nowrap py-0 font-bold tracking-tight">
            {t(`${title}`)}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <div>
            <Textarea
              name="Text1"
              wrap="soft"
              placeholder={t(
                title === "Request access"
                  ? `Share a quick message using this form and we’ll enable free access for you to try Level2. No commitment, no credit card required—just visual tools, powerful strategies, and a fresh take on systematic trading.`
                  : title === "Contact sales"
                  ? "If you're a broker and want to learn more about our pricing, features, or integration options, just send a message using this form. A member of our team will be in touch to tailor a solution that fits your needs."
                  : "We're here to help with any issues or questions you might have. Please share a message using this form, and our technical support team will assist you as quickly as possible."
              )}
              onChange={(e) => setMessage(e.target.value)}
              value={message}
              className="w-full h-full max-h-[400px]"
              rows={10}
            />
            <div>
              {messageError && (
                <p className="text-destructive text-sm">Message is required.</p>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter className="grow px-0">
          <div className="flex w-full h-full">
            <div className="flex   gap-3 w-full float-right h-full">
              {showTechnicalRequest.helpline && !details?.isWhiteLabled && (
                <div className="flex md:mt-10 flex-col gap-3">
                  <p className="text-lg font-bold">{t("Execution Helpline")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "If you need immediate assistance with order execution our dedicated support team is available 24/7 to ensure seamless and efficient trading (weekdays only)."
                    )}
                  </p>
                  <div>
                    <Button
                      variant={"secondary"}
                      onClick={() =>
                        window.open("https://t.me/+fiNoY-Jscv8yMzI0", "_blank")
                      }
                      className="bg-[--accent-secondary] hover:bg-[--accent-secondary] mt-auto w-auto text-white"
                    >
                      {t("Access group")}
                    </Button>
                  </div>
                </div>
              )}

              {details?.isWhiteLabled && (
                <p className="text-sm text-muted-foreground">{`Please send an email to ${details?.supportEmail} to get in touch with the support team`}</p>
              )}

              <div className="flex flex-col w-full lg:flex-row float-right">
                <div className="w-full">
                  <Button
                    disabled={messageLoader}
                    onClick={onSubmit}
                    className="text-sm gap-2 float-right"
                  >
                    {t("Submit")}
                    {messageLoader && (
                      <Loader2 className="ml-2 h-5 w-5 animate-spin" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TechnicalRequestModal;
